import { type Project } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { EntityIcon, iconTintStyle } from '@/components/ui/icon-picker';
import { Tooltip } from '@/components/ui/Tooltip';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import {
  PROJECT_LABELS,
  ProjectDropdownItems,
  useProjectsMenuScrollLoadMore,
} from '@/features/projects/move-to-project';
import { type ProjectMenuOption } from '@/features/projects/project-menu-option';
import { cn } from '@/lib/utils';

export const ComposerProjectDropdown = observer(
  function ComposerProjectDropdown({
    selectedProject,
    projectOptions,
    onSelectProject,
    onCreateProject,
    borderless = false,
  }: {
    selectedProject: Project | null;
    projectOptions: ProjectMenuOption[];
    onSelectProject: (projectId: string | null) => Promise<void> | void;
    onCreateProject: () => void;
    borderless?: boolean;
  }) {
    const onProjectsMenuScroll = useProjectsMenuScrollLoadMore();
    const label = selectedProject?.name ?? PROJECT_LABELS.add;

    return (
      <DropdownMenu.Root>
        {borderless ? (
          <Tooltip label={label}>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className={cn(
                  'inline-flex size-5 items-center justify-center rounded-md',
                  'smoothing-default text-foreground-subtle transition-colors',
                  'hover:bg-surface-hover focus-ring'
                )}
              >
                {selectedProject ? (
                  <EntityIcon icon={selectedProject.icon} size={16} />
                ) : (
                  <Icons.folderPlus className="size-4" />
                )}
              </button>
            </DropdownMenu.Trigger>
          </Tooltip>
        ) : (
          <DropdownMenu.Trigger asChild>
            <RoundIconButton
              icon={
                selectedProject ? (
                  <EntityIcon icon={selectedProject.icon} size={16} />
                ) : (
                  Icons.folderPlus
                )
              }
              size="sm"
              label={label}
              active={Boolean(selectedProject)}
              style={
                selectedProject
                  ? iconTintStyle(selectedProject.icon?.color)
                  : undefined
              }
            />
          </DropdownMenu.Trigger>
        )}
        <DropdownMenu.Content
          align="start"
          side="bottom"
          fit
          className="min-w-44 max-w-64"
          onScroll={onProjectsMenuScroll}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <ProjectDropdownItems
            projectOptions={projectOptions}
            onCreateProject={onCreateProject}
            onSelectProject={(id) => void onSelectProject(id)}
            onClearProject={() => void onSelectProject(null)}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);
